// The REQUIRED-update state machine (KAN-887 / RD-OPS-008). When /version.json
// says this copy is too old (`updateRequired`), in order:
//   1. a safe moment (the silent-update gate) → reload quietly, no card;
//   2. otherwise show the blocking card. If unsaved work exists at that moment
//      the card is the SAVE-FIRST variant, whose "Finish and save (2 min)" hides
//      it ONCE for 2 minutes (in-memory — a reload resets it, which is fine);
//      during those minutes a safe moment still reloads quietly;
//   3. after the 2 minutes (or if no unsaved work) it is the plain blocking card.
// Only signed-in app paths take part: elsewhere nothing happens (those pages
// reload fresh). The card itself is aria-modal, so the gate is NOT polled while
// it shows — the user's "Update" tap is the way out.

import { useCallback, useEffect, useRef, useState } from 'react';
import { isSignedInAreaPath } from '../utils/app/silentUpdateGate';
import { hasUnsavedWork } from '../utils/app/unsavedWorkRegistry';
import { hasLiveTypedField, isEditableElement } from '../utils/app/editableTypingTracker';

export const FINISH_AND_SAVE_MS = 2 * 60_000;
const POLL_MS = 500;

function unsavedNow() {
  try {
    return hasUnsavedWork() || hasLiveTypedField() || isEditableElement(document.activeElement);
  } catch (_) {
    return true; // fail closed: offer the save-first variant
  }
}

/**
 * @param {{ updateRequired: boolean, reloadQuietlyNow: () => boolean, silentReloading?: boolean }} args
 * @returns {{ cardVisible: boolean, saveFirst: boolean, finishAndSave: () => void }}
 */
export function useRequiredUpdateFlow({ updateRequired, reloadQuietlyNow, silentReloading = false }) {
  // 'pending' → (card | reloading); card(saveFirst) → 'deferred' → card(blocking)
  const [phase, setPhase] = useState('pending');
  const [saveFirst, setSaveFirst] = useState(false);
  const deferredUsedRef = useRef(false);
  const untilRef = useRef(0);
  const reloadRef = useRef(reloadQuietlyNow);
  reloadRef.current = reloadQuietlyNow;

  useEffect(() => {
    if (!updateRequired || (phase !== 'pending' && phase !== 'deferred')) return undefined;
    const tick = () => {
      if (!isSignedInAreaPath(String(window.location.pathname || ''))) return;
      if (reloadRef.current()) {
        setPhase('reloading');
        return;
      }
      if (phase === 'pending') {
        setSaveFirst(unsavedNow());
        setPhase('card');
      } else if (Date.now() >= untilRef.current) {
        setSaveFirst(false);
        setPhase('card');
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, [updateRequired, phase]);

  // A quiet reload can still bail at its last-moment re-check (a save started
  // in the gap): once the splash flag has risen and dropped again, start over.
  const sawReloadingRef = useRef(false);
  useEffect(() => {
    if (phase !== 'reloading') {
      sawReloadingRef.current = false;
      return;
    }
    if (silentReloading) sawReloadingRef.current = true;
    else if (sawReloadingRef.current) setPhase('pending');
  }, [phase, silentReloading]);

  const finishAndSave = useCallback(() => {
    if (deferredUsedRef.current) return;
    deferredUsedRef.current = true;
    untilRef.current = Date.now() + FINISH_AND_SAVE_MS;
    setPhase('deferred');
  }, []);

  return {
    cardVisible: updateRequired && phase === 'card',
    saveFirst: saveFirst && !deferredUsedRef.current,
    finishAndSave,
  };
}
